import { useEffect, useMemo, useState } from 'react'
import { FieldsPicker } from '../components/FieldsPicker.js'
import { SlideOver } from '../components/SlideOver.js'
import { fetchCatalogue } from '../lib/api.js'
import { formatGBP, formatPct } from '@shared/money'
import { checkSellPrice, costMissing, FREQUENCY_SUFFIX, margin, priceProduct, termLabel } from '@shared/pricing'
import { FREQUENCY_LABEL } from '@shared/billing'
import type { Partner, PricedProduct, Tier } from '@shared/types'

/**
 * The tier-priced catalogue — the Foundation deliverable that proves the pricing
 * engine end to end.
 *
 * ⚠️ Buy price and margin columns are suppressed for agent mode: agents quote at
 * list and never buy, so showing them Wave9's discount ladder would leak the
 * commercial model with no upside.
 */

type FieldKey = 'category' | 'list' | 'discount' | 'buy' | 'margin' | 'billing' | 'term'

/**
 * SKU is the first column and is not optional: it's how Wave9 and its partners
 * actually refer to a product ("WTHS-3YR-AR"), and the names are long enough
 * that scanning by name alone is slow.
 */
const ALL_FIELDS: Array<{ key: FieldKey; label: string; locked?: boolean }> = [
  { key: 'category', label: 'Product Line' },
  { key: 'billing', label: 'Billing' },
  { key: 'term', label: 'Term (months)' },
  { key: 'list', label: 'List price', locked: true },
  { key: 'discount', label: 'Your discount' },
  { key: 'buy', label: 'Cost / buy price' },
  { key: 'margin', label: 'Margin at list' },
]

export function Catalogue({ partner }: { partner: Partner }) {
  const [data, setData] = useState<PricedProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('all')
  const [billing, setBilling] = useState('all')
  const [term, setTerm] = useState('all')
  const [view, setView] = useState<'table' | 'tiles'>('table')
  const [selected, setSelected] = useState<PricedProduct | null>(null)
  const [tier, setTier] = useState<Tier | null>(null)
  const [tiers, setTiers] = useState<Tier[]>([])

  const showBuy = partner.mode !== 'agent'
  const internal = partner.mode === 'internal'
  // Wave9 quoting directly isn't "buying" from anyone — the cost basis is its
  // own unit cost (decision 53), and calling that "your buy price" would read
  // as a supplier price that doesn't exist.
  const costLabelShort = internal ? 'Cost' : 'Your buy'

  /**
   * ⚠️ EVERY product missing a cost is a different problem from SOME missing one.
   *
   * Some (22 of 157) is data entry, and a per-row badge is the right nudge. ALL
   * of them means the cost column has never actually been populated: rows in
   * products_cache written by a sync that predates the column keep their NULL
   * until a later sync overwrites them, so adding the column and the display
   * changed nothing on screen. 157 identical badges bury that under noise and
   * read as "HubSpot has no costs" — which is false, and sends you to the price
   * book instead of to the sync button.
   */
  const noCostAtAll = internal && data.length > 0 && data.every((p) => costMissing(p))

  const [fields, setFields] = useState<Set<FieldKey>>(
    () => new Set<FieldKey>(['category', 'billing', 'term', 'list', 'discount', 'buy']),
  )

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchCatalogue()
      .then((res) => {
        if (!live) return
        setData(res.products)
        setTier(res.tier)
        setTiers(res.tiers ?? [])
        setError(null)
      })
      .catch((e: Error) => live && setError(e.message))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [partner.id])

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(data.map((p) => p.category ?? 'Uncategorised'))).sort()],
    [data],
  )

  // Read the terms actually in the price book rather than assuming 12/36/60 —
  // the contract term is baked into the product, so this set is the real menu.
  const terms = useMemo(
    () => Array.from(new Set(data.filter((p) => p.termMonths != null).map((p) => p.termMonths!)))
      .sort((a, b) => a - b),
    [data],
  )

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return data.filter((p) => {
      if (category !== 'all' && (p.category ?? 'Uncategorised') !== category) return false
      if (billing === 'recurring' && !p.recurring) return false
      if (billing === 'oneoff' && p.recurring) return false
      if (term === 'oneoff' && p.termMonths != null) return false
      if (term !== 'all' && term !== 'oneoff' && String(p.termMonths ?? '') !== term) return false
      if (!q) return true
      return `${p.name} ${p.sku ?? ''} ${p.description ?? ''}`.toLowerCase().includes(q)
    })
  }, [data, query, category, billing, term])

  const show = (k: FieldKey) => fields.has(k) && (showBuy || (k !== 'buy' && k !== 'discount' && k !== 'margin'))

  if (loading) return <p className="muted">Loading catalogue…</p>
  if (error) return <p className="pill pill--stop">{error}</p>

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Catalogue</h1>
          <p>
            {partner.mode === 'agent' && 'Agent mode — quotes go out at list price. Wave9 invoices the end customer and you earn a referral fee per accepted quote.'}
            {partner.mode === 'reseller' && `Reseller mode — ${tier?.name ?? 'no'} tier. Your buy price is the floor; you set the sell price above it.`}
            {partner.mode === 'internal' && 'Internal mode — Wave9 staff. Margin is measured against Wave9 unit cost; pricing is fully editable and ad-hoc lines are allowed.'}
          </p>
        </div>
        <div className="toolbar">
          <div className="toggle" role="group" aria-label="View">
            <button aria-pressed={view === 'table'} onClick={() => setView('table')}>Table</button>
            <button aria-pressed={view === 'tiles'} onClick={() => setView('tiles')}>Tiles</button>
          </div>
          <FieldsPicker fields={ALL_FIELDS} visible={fields} onChange={setFields} />
        </div>
      </div>

      {noCostAtAll && (
        <div className="banner banner--stop">
          <strong>No unit costs have been synced.</strong>
          <span>
            Not one of these {data.length} products has a cost, so every margin here reads as 100% against a £0 basis.
            HubSpot does hold costs — these rows were cached before the cost column existed and keep
            their old empty value until a sync rewrites them. Run <strong>Admin → Scheduled jobs →
            Catalogue sync</strong>; a dry run will say how many costs it can see before it writes
            anything. If it still reports every product as costless, the HubSpot token can't read{' '}
            <code>hs_cost_of_goods_sold</code> and it's a scope problem, not a data one.
          </span>
        </div>
      )}

      {showBuy && (
        <div className="banner">
          <strong>Commercially sensitive.</strong>
          <span>
            {internal
              ? 'Wave9 unit costs are Wave9-internal — never shown to a partner, a customer quote page or a PDF.'
              : 'Buy prices and discounts on this page are yours alone — they never appear on a customer quote page or PDF.'}
          </span>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <div className="toolbar">
            <input
              className="input"
              placeholder="Search products…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>)}
            </select>
            <select className="select" value={billing} onChange={(e) => setBilling(e.target.value)}>
              <option value="all">All billing</option>
              <option value="recurring">Recurring (annual)</option>
              <option value="oneoff">One-off</option>
            </select>
            <select className="select" value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Contract term">
              <option value="all">All terms</option>
              <option value="oneoff">No term (one-off)</option>
              {terms.map((t) => (
                <option key={t} value={String(t)}>{t} months ({t / 12} yr)</option>
              ))}
            </select>
          </div>
          <span className="muted small">{rows.length} of {data.length} products</span>
        </div>

        {view === 'table' ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  {show('category') && <th>Product Line</th>}
                  {show('billing') && <th>Billing</th>}
                  {show('term') && <th className="num">Term</th>}
                  <th className="num">List</th>
                  {show('discount') && <th className="num">Discount</th>}
                  {show('buy') && <th className="num">{costLabelShort}</th>}
                  {show('margin') && <th className="num">Margin at list</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const m = margin(p.listPrice, p.buyPrice)
                  return (
                    <tr key={p.hsProductId} onClick={() => setSelected(p)}>
                      <td className="small"><strong>{p.sku ?? '—'}</strong></td>
                      <td>{p.name}</td>
                      {show('category') && <td className="muted">{p.category ?? '—'}</td>}
                      {show('billing') && (
                        <td>
                          {p.recurring
                            ? <span className="pill pill--info">annual</span>
                            : <span className="pill">one-off</span>}
                        </td>
                      )}
                      {show('term') && (
                        <td className="num">
                          {p.termMonths != null
                            ? <>{p.termMonths} <span className="muted small">({termLabel(p.termMonths)})</span></>
                            : <span className="muted">—</span>}
                        </td>
                      )}
                      <td className="num">{formatGBP(p.listPrice)}</td>
                      {show('discount') && <td className="num muted">{formatPct(p.effectiveDiscountPct)}</td>}
                      {show('buy') && (
                        <td className="num">
                          <strong>{formatGBP(p.buyPrice)}</strong>
                          {/* Suppressed when it's universal — the banner says it once instead. */}
                          {internal && !noCostAtAll && costMissing(p) && (
                            <span className="pill pill--warn" style={{ marginLeft: 6 }} title="No unit cost in HubSpot — treated as £0 (05), so this margin is indicative">
                              no cost
                            </span>
                          )}
                        </td>
                      )}
                      {show('margin') && (
                        <td className="num">{formatGBP(m.marginAmount)} <span className="muted small">({formatPct(m.marginPct)})</span></td>
                      )}
                    </tr>
                  )
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={3 + (['category', 'billing', 'term', 'discount', 'buy', 'margin'] as FieldKey[]).filter(show).length} className="muted" style={{ padding: 20 }}>
                      Nothing matches those filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card__body">
            <div className="cards-grid">
              {rows.map((p) => (
                <div key={p.hsProductId} className="mini-card" onClick={() => setSelected(p)}>
                  <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>{p.sku ?? '—'}</div>
                  <h3 style={{ fontWeight: 400 }}>{p.name}</h3>
                  <div className="muted small" style={{ marginBottom: 8 }}>
                    {p.category ?? 'No Product Line'}
                    {p.termMonths != null && ` · ${p.termMonths} months`}
                  </div>
                  <div className="num" style={{ textAlign: 'left', fontSize: 17, fontWeight: 600 }}>
                    {formatGBP(showBuy ? p.buyPrice : p.listPrice)}
                    <span className="muted small" style={{ fontWeight: 400 }}>
                      {' '}{showBuy ? (internal ? 'cost' : 'your buy') : 'list'}{p.recurring ? ' / year' : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selected && <ProductPanel product={selected} partner={partner} tier={tier} tiers={tiers} onClose={() => setSelected(null)} />}
    </>
  )
}

/**
 * Product detail with a live sell-price check — the same guardrail the builder
 * will use in phase 2, exercised here so the rules are visible now.
 */
function ProductPanel({
  product, partner, tier, tiers, onClose,
}: {
  product: PricedProduct
  partner: Partner
  tier: Tier | null
  tiers: Tier[]
  onClose: () => void
}) {
  const [sell, setSell] = useState(String(product.defaultSellPrice))
  const parsed = Number(sell)
  const check = checkSellPrice(partner.mode, {
    listPrice: product.listPrice,
    buyPrice: product.buyPrice,
    sellPrice: Number.isFinite(parsed) ? parsed : 0,
  })
  const m = margin(check.ok ? parsed : check.corrected, product.buyPrice)
  const showBuy = partner.mode !== 'agent'
  const internal = partner.mode === 'internal'
  // Wave9 quoting directly isn't "buying" from anyone — the cost basis is its
  // own unit cost (decision 53), and calling that "your buy price" would read
  // as a supplier price that doesn't exist.
  const costLabel = internal ? 'Wave9 cost' : 'Your buy price'

  // What this product would cost under each tier — makes the ladder concrete.
  const ladder = tiers.map((t) => ({
    tier: t,
    price: priceProduct(product, { mode: 'reseller', tier: t }).buyPrice,
  }))

  return (
    <SlideOver
      title={product.name}
      subtitle={`${product.category ?? 'Uncategorised'} · ${product.sku ?? 'no SKU'}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn btn--primary" disabled title="Quote builder arrives in phase 2">Add to quote</button>
        </>
      }
    >
      {product.description && <p className="small" style={{ marginTop: 0 }}>{product.description}</p>}

      <dl className="kv" style={{ marginTop: 16 }}>
        <dt>List price</dt>
        <dd>{formatGBP(product.listPrice)}{product.recurring ? ` ${FREQUENCY_SUFFIX[product.billingFrequency ?? 'annually']}` : ''}</dd>
        <dt>Billing</dt>
        <dd>{product.recurring ? `${FREQUENCY_LABEL[product.billingFrequency ?? 'annually']}, ${termLabel(product.termMonths ?? 12)} term` : 'One-off'}</dd>
        {showBuy && (
          <>
            {!internal && (
              <>
                <dt>Your tier</dt>
                <dd>{tier ? `${tier.name} (×${tier.multiplier})` : <span className="pill pill--warn">no tier set</span>}</dd>
                <dt>Base discount</dt>
                <dd>{formatPct(product.baseDiscountPct)}</dd>
                <dt>Your discount</dt>
                <dd><strong>{formatPct(product.effectiveDiscountPct)}</strong></dd>
              </>
            )}
            <dt>{costLabel}</dt>
            <dd>
              <strong>{formatGBP(product.buyPrice)}</strong>
              {internal && costMissing(product) && (
                <div className="muted small" style={{ marginTop: 4 }}>
                  No unit cost in HubSpot, so this falls back to list and margin reads as zero.
                  Set <code>hs_cost_of_goods_sold</code> on the product.
                </div>
              )}
              {internal && !costMissing(product) && product.costVerified === false && (
                <span className="pill pill--warn" style={{ marginLeft: 6 }}>unverified</span>
              )}
            </dd>
          </>
        )}
      </dl>

      <h3 style={{ marginTop: 22, marginBottom: 8 }}>Sell price</h3>
      {product.sellLocked ? (
        <p className="small">
          <span className="pill pill--info">list-locked</span>{' '}
          Agent quotes always go out at list. You earn a fixed referral fee per accepted quote
          {partner.referralFee != null && <> — currently {formatGBP(partner.referralFee)}</>}.
        </p>
      ) : (
        <>
          <div className="toolbar">
            <input
              className="input"
              type="number"
              step="0.01"
              value={sell}
              onChange={(e) => setSell(e.target.value)}
              style={{ width: 140 }}
              aria-label="Sell price"
            />
            <span className="muted small">
              floor {product.sellFloor == null ? 'none' : formatGBP(product.sellFloor)}
            </span>
          </div>
          {!check.ok && (
            <p className="pill pill--stop" style={{ marginTop: 10, display: 'inline-block' }}>{check.reason}</p>
          )}
          {showBuy && check.ok && (
            <p className="small" style={{ marginTop: 10 }}>
              Margin <strong>{formatGBP(m.marginAmount)}</strong>{' '}
              <span className="muted">({formatPct(m.marginPct)})</span>
              {product.recurring && <span className="muted"> per year</span>}
            </p>
          )}
        </>
      )}

      {/* Decision 138: the ladder renders only when the server sent one —
          STAFF sessions only. A partner session's payload carries no tiers,
          so a reseller can never see another tier's pricing, by shape. */}
      {showBuy && !internal && ladder.length > 0 && (
        <>
          <h3 style={{ marginTop: 22, marginBottom: 8 }}>Tier ladder</h3>
          <table>
            <thead><tr><th>Tier</th><th className="num">Multiplier</th><th className="num">Buy price</th></tr></thead>
            <tbody>
              {ladder.map(({ tier: t, price }) => (
                <tr key={t.id} style={t.id === tier?.id ? { fontWeight: 600 } : undefined}>
                  <td>{t.name}{t.id === tier?.id && <span className="pill pill--info" style={{ marginLeft: 6 }}>yours</span>}</td>
                  <td className="num">×{t.multiplier}</td>
                  <td className="num">{formatGBP(price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small" style={{ marginTop: 10 }}>
            Wave9 staff view — partner sessions never receive other tiers' pricing.
          </p>
        </>
      )}
    </SlideOver>
  )
}
